import React, { useRef, useState, useEffect, useCallback } from 'react';
import "../../editorStyleSheet.css"
import PropTypes from "prop-types";
import { getScriptDirectionJoditEditor, extractArticleInner, injectArticleInner } from "../../../../utlis/generalUtils";
import CircularProgress from '@mui/material/CircularProgress';
import AudioPlayer from "../wavesurfer/AudioPlayer";
import 'react-edit-text/dist/index.css';
import SoftBox from "../../../../components/SoftBox";
import {IconButton, Tooltip, useMediaQuery, useTheme} from "@mui/material";
import { Undo2, Redo2, Languages, FileText } from "lucide-react";
import { Box, Button, ButtonGroup } from "@mui/material"
import RegenerateServicesPopup from "../editorToolbarComponents/RegenerateServicesPopup";
import { useTranslation } from "react-i18next";


import JoditEditor from 'jodit-react';
import 'jodit/es5/jodit.css';
import { FaEdit, FaSave } from 'react-icons/fa';
import ChatBot from "../../../chatbot/ChatBot";
import { uploadToGoogleStorage } from "../../../../axios-client";

const DigitizationHtmlComponent = ({
                                       pageNumber, containerWidth, configWidth, updateConfigWidth, page, sourceLanguage, targetLanguage, addOrUpdatePages, updatedPages,
                                       handleSave, regenerateBookmarking, bookmarkingSelected, translationSelected, diacriticsSelected,  digitizationId, numPages, fileName, fetchData, setNotification, setPage 
                                   }) => {

    const { t, i18n } = useTranslation(['editor']);
    const dir = i18n.dir();
    const editorRef = useRef(null);  
    const [displayExtractedText, setDisplayExtractedText] = useState(true);
    const [htmlContent, setHtmlContent] = useState('');
    const [translatedContent, setTranslatedContent] = useState('');
    const [loading, setLoading] = useState(false);
    const [audioUrl, setAudioUrl] = useState('');
    const pageHistoriesRef = useRef({});

    const [canUndo, setCanUndo] = useState(false);
    const [canRedo, setCanRedo] = useState(false);



    const fullHtmlTemplateRef = useRef("");
    const fullTranslatedTextTemplateRef = useRef("");

    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const isRtl = dir === "rtl" ;

    const editor = useRef(null);
    const [isEditing, setIsEditing] = useState(false);

    const handlePaste = (event) => {
        const clipboard = event.clipboardData;
        if (!clipboard) return;

        const editorInstance = editorRef.current;
        if (!editorInstance) return;

        const text = clipboard.getData("text/plain");
        const items = clipboard.items;

        editorInstance.selection.focus();

        for (let i = 0; i < items.length; i++) {
            const item = items[i];

            // IMAGE
            if (item.type.includes("image")) {
                event.preventDefault();

                const file = item.getAsFile();

                uploadToGoogleStorage(file).then((res) => {
                    const imageUrl = res.data?.path;
                    if (!imageUrl) return;

                    editorInstance.selection.insertHTML(
                        `<img src="${imageUrl}" alt="pasted image" />`
                    );
                });

                return;
            }
        }

        // TEXT
        if (text) {
            event.preventDefault();

            const safeText = text.replace(/\n/g, "<br>");
            editorInstance.selection.insertHTML(safeText);
        }
    };

    const config = {
        iframe: true,
        readonly: false,
        maxHeight: 700,
        history: false,
        direction: displayExtractedText ? getScriptDirectionJoditEditor(sourceLanguage) : getScriptDirectionJoditEditor(targetLanguage),
        toolbarSticky: false,
        removeButtons: ["source"],
        buttons: [
            "bold", "italic", "underline", "|",
            "ul", "ol", "|",
            "table", "tableprops", "tablecellprops", "tablerowprops", "tabledelete", "|",
            "align", "image"
        ],
        cleanHTML: {
            removeEmptyElements: false,
            fillEmptyParagraph: false
        },
        allowPasteImage: true,
        events: {
            afterInit: (editorInstance) => {
                editorRef.current = editorInstance;
            },
            mouseup: () => {
                editorRef.current?.s.save();
            },
            keyup: () => {
                editorRef.current?.s.save();
            },
            focus: () => {
                editorRef.current?.s.save();
            },
            paste: handlePaste
        }
    };

    const handleToggleEdit = () => {
        setIsEditing(!isEditing);
    };

    const checkPageType = (currentPage) => {
        switch (currentPage.type) {
            case "NO_TEXT":
                setHtmlContent("<p>No TEXT.</p>");
                setTranslatedContent("No TEXT.");
                break;
            case "TEXT":
                if (currentPage.html) {
                    fullHtmlTemplateRef.current = currentPage.html;
                    setHtmlContent(extractArticleInner(currentPage.html));
                } else {
                    setHtmlContent("<p>No OCR is available.</p>");
                }
                if (currentPage.translated_text) {
                    fullTranslatedTextTemplateRef.current = currentPage.translated_text;
                    setTranslatedContent(extractArticleInner(currentPage.translated_text));
                } else {
                    setTranslatedContent("<p>No Translation is available.</p>");
                }
                break;
            default:
                setHtmlContent(currentPage.html ?? "No OCR is available.")
                setTranslatedContent(currentPage.translated_text ?? "No Translation is available.");

        }
    }

    const handleContentSectionMouseDown = useCallback((e) => {
        e.preventDefault();
        const initialWidth = e.target.parentElement.offsetWidth;
        const initialDivWidth = e.target.offsetParent.offsetWidth;
        const initialX = e.clientX;

        const handleMouseContentSectionMove = (e) => {
            let newWidth = initialWidth - (e.clientX - initialX);

            if (newWidth < initialDivWidth * 0.2) {
                newWidth = (initialDivWidth * 0.2);
            } else if (newWidth > initialDivWidth * 0.75) {
                newWidth = (initialDivWidth * 0.75);
            }

            const contentSection = document.getElementById("digitizationEditorHTMLSection");
            if (contentSection && contentSection.offsetWidth !== newWidth) {
                contentSection.style.width = newWidth + 'px';
                updateConfigWidth(newWidth);
            }
            const event = new CustomEvent('configResized');
            window.dispatchEvent(event);
        };

        const handleMouseContentSectionUp = () => {
            document.removeEventListener('mousemove', handleMouseContentSectionMove);
            document.removeEventListener('mouseup', handleMouseContentSectionUp);
        };

        document.addEventListener('mousemove', handleMouseContentSectionMove);
        document.addEventListener('mouseup', handleMouseContentSectionUp);
    }, [updateConfigWidth]);


    useEffect(() => {
        if (page) {
            checkPageType(page);
            setAudioUrl(page.audio_path);

            pageHistoriesRef.current = {}
            const htmlKey = `${pageNumber}_html`;
            const translatedKey = `${pageNumber}_translated_text`;

            if (page.html) {
                pageHistoriesRef.current[htmlKey] = {
                    history: [extractArticleInner(page.html)],
                    index: 0,
                };
            }

            if (page.translated_text) {
                pageHistoriesRef.current[translatedKey] = {
                    history: [extractArticleInner(page.translated_text)],
                    index: 0,
                };
            }

            setCanUndo(false);
            setCanRedo(false);
        }
    }, [page, pageNumber]);


    const updateHtml = (articleInnerHtml) => {
        if (page.type === 'TEXT' || page.type === null) {
            const fullHtml = injectArticleInner(
                fullHtmlTemplateRef.current,
                articleInnerHtml
            );

            addOrUpdatePages(pageNumber, fullHtml, 'html');
        }
    };

    const updateTranslatedHtml = (articleInnerHtml) => {
        if (page.type === 'TEXT' || page.type === null) {
            const fullHtml = injectArticleInner(
                fullTranslatedTextTemplateRef.current,
                articleInnerHtml
            );

            addOrUpdatePages(pageNumber, fullHtml, 'translated_text');
        }
    };

    const updateUndoRedoState = (key) => {
        const h = pageHistoriesRef.current[key];
        if (!h) return;

        const newCanUndo = h.index > 0;
        const newCanRedo = h.index < h.history.length - 1;

        setCanUndo(prev => prev !== newCanUndo ? newCanUndo : prev);
        setCanRedo(prev => prev !== newCanRedo ? newCanRedo : prev);
    };


    const handleSwitchChange = (value) => {
        setDisplayExtractedText(value);
    };

    useEffect(() => {
        if (page) {
            checkPageType(page);
            setAudioUrl(page.audio_path);
        }
    }, [page]);

    const handleSaveWithLoading = () => {
        setIsEditing(false)
        if (updatedPages.length > 0) {
            setLoading(true);
            handleSave()
                .then(() => {
                    const existingPage = updatedPages.find(item => item.num === page.num);
                    setPage({
                        ...page,
                        html: existingPage.html,
                        translated_text: existingPage.translated_text ?? page.translated_text,
                    })
                })
                .catch((err) => {
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    };

    const handleUndo = () => {
        const type = displayExtractedText ? "html" : "translated_text";
        const key = `${pageNumber}_${type}`;
        const pageHistory = pageHistoriesRef.current[key];

        if (!pageHistory || pageHistory.index <= 0) return;

        const newIndex = pageHistory.index - 1;
        const content = pageHistory.history[newIndex];

        if (displayExtractedText) {
            setHtmlContent(content);
        } else {
            setTranslatedContent(content);
        }

        const fullHtml = injectArticleInner(
            !displayExtractedText ? fullTranslatedTextTemplateRef.current :
                fullHtmlTemplateRef.current,
            content
        );

        addOrUpdatePages(pageNumber, fullHtml, type);

        pageHistory.index = newIndex;
        updateUndoRedoState(key);
    };

    const handleRedo = () => {
        const type = displayExtractedText ? "html" : "translated_text";
        const key = `${pageNumber}_${type}`;
        const pageHistory = pageHistoriesRef.current[key];

        if (!pageHistory || pageHistory.index >= pageHistory.history.length - 1) return;

        const newIndex = pageHistory.index + 1;
        const content = pageHistory.history[newIndex];

        if (displayExtractedText) {
            setHtmlContent(content);
        } else {
            setTranslatedContent(content);
        }

        const fullHtml = injectArticleInner(
            !displayExtractedText ? fullTranslatedTextTemplateRef.current :
                fullHtmlTemplateRef.current,
            content
        );

        addOrUpdatePages(pageNumber, fullHtml, type);

        pageHistory.index = newIndex;
        updateUndoRedoState(key);
    };

    const pushToHistory = (page, content, type) => {
        const key = `${page}_${type}`;
        const pageHistory = pageHistoriesRef.current[key] || { history: [], index: -1 };

        if (pageHistory.history[pageHistory.index] === content) return;

        const newHistory = pageHistory.history.slice(0, pageHistory.index + 1);
        newHistory.push(content);

        pageHistoriesRef.current[key] = {
            history: newHistory,
            index: newHistory.length - 1,
        };

        updateUndoRedoState(key);
    };


    return (
        <div className="digitizationEditorHTMLSection" id="digitizationEditorHTMLSection"
             style={{
                 display: 'flex', flexDirection: 'column', maxHeight: '100vh',
                 minWidth: "20%", width: "100%"
             }}
        >
            <div className="resize-handle" onMouseDown={handleContentSectionMouseDown} />
            <Box sx={{
                borderBottom: "1px solid #e0d8d8b0",
                px: { xs: 1, sm: 2 },
                py: { xs: 0.5, sm: 1 },
                height: { xs: "auto", sm: "3.5rem" },
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: { xs: "wrap", md: "nowrap" },
                gap: { xs: 1, sm: 2 },
                minWidth: "max-content",
            }}
                 dir={dir}
            >
                <Box sx={{display: "flex", alignItems: "center", gap: 1}}>

                    <Tooltip title={"undo"}>
                        <span>
                            <IconButton onClick={handleUndo} disabled={!canUndo}
                            variant="outlined"
                            size={isMobile ? "small" : "medium"}
                            sx={{
                                width: { xs: 28, sm: 32 },
                                height: { xs: 28, sm: 32 },
                                fontSize: { xs: "0.65rem", sm: "0.75rem" },
                                color: "#616161",
                                border: "1px solid #d8d6d6",
                                borderRadius: "5px",
                                p: 1,
                                "&:hover": {
                                    borderColor: "#757575",
                                    background: "#F5F5F5",
                                },
                                "&:disabled": {
                                    opacity: 0.6,
                                }
                            }}

                            >
                            <Undo2 size={18}/>
                            </IconButton>
                        </span>
                    </Tooltip>

                    <Tooltip title={"redo"}>
                        <span>
                            <IconButton onClick={handleRedo} disabled={!canRedo}
                            variant="outlined"
                            size={isMobile ? "small" : "medium"}
                            sx={{
                                width: { xs: 28, sm: 32 },
                                height: { xs: 28, sm: 32 },
                                fontSize: { xs: "0.65rem", sm: "0.75rem" },
                                color: "#616161",
                                border: "1px solid #d8d6d6",
                                borderRadius: "5px",
                                p: 1,
                                "&:hover": {
                                    borderColor: "#757575",
                                    background: "#F5F5F5",
                                },
                                "&:disabled": {
                                    opacity: 0.6,
                                }
                            }}

                            >
                            <Redo2 size={18} />
                            </IconButton>

                        </span>
                    </Tooltip>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2}}>
                    <ButtonGroup
                        variant="outlined"
                        disableElevation
                        sx={{
                            bgcolor: "#ffffff !important",
                            "& .MuiButton-root": {
                                height: 32,
                                fontSize: "0.75rem",
                                color: "#616161", // grey.700
                                borderColor: "#bdbdbd !important", // grey.400
                                "&:hover": {
                                    borderColor: "#757575 !important", // grey.600
                                    bgcolor: "#f5f5f5 !important", // grey.100
                                },
                                "&.Mui-focusVisible, &:focus": {
                                    outline: "none !important",
                                    boxShadow: "none !important",
                                },
                            },
                            "& .MuiButton-contained": {
                                bgcolor: "#424242 !important", // grey.800
                                color: "#ffffff",
                                "&:hover": {
                                    bgcolor: "#212121 !important",
                                    borderColor: "#bdbdbd !important",// grey.900
                                },
                            },
                            "&.Mui-focusVisible, &:focus": {
                                bgcolor: "#212121 !important",
                                borderColor: "#bdbdbd !important",
                                outline: "none !important",
                                boxShadow: "none !important",
                            },
                            "& .MuiButtonGroup-firstButton": {
                                borderTopLeftRadius: isRtl ? 0 : 4,
                                borderBottomLeftRadius: isRtl ? 0 : 4,
                                borderTopRightRadius: isRtl ? 4 : 0,
                                borderBottomRightRadius: isRtl ? 4 : 0,
                            },
                            "& .MuiButtonGroup-lastButton": {
                                borderTopRightRadius: isRtl ? 0 : 4,
                                borderBottomRightRadius: isRtl ? 0 : 4,
                                borderTopLeftRadius: isRtl ? 4 : 0,
                                borderBottomLeftRadius: isRtl ? 4 : 0,
                            },
                        }}
                    >
                        <Button
                            variant={displayExtractedText ? "contained" : "outlined"}
                            onClick={() => handleSwitchChange(true)}
                            startIcon={<FileText size={isMobile ? 10 : 12} />}
                            sx={{
                                fontSize: { xs: "0.65rem", sm: "0.75rem" },
                                px: { xs: 1, sm: 2 },
                                minWidth: { xs: 32, sm: "auto" },
                                "& .MuiButton-startIcon": {
                                    marginLeft: dir === "rtl" ? "0.3rem" : "-4px",
                                    marginRight: dir === "rtl" ? "0px" : "8px"
                                },
                            }}
                        >
                            {isMobile ? "E" : t("extracted_text")}
                        </Button>

                        {translationSelected && (
                            <Button
                                variant={!displayExtractedText ? "contained" : "outlined"}
                                onClick={() => handleSwitchChange(false)}
                                startIcon={<Languages size={isMobile ? 10 : 12} />}
                                sx={{
                                    fontSize: { xs: "0.65rem", sm: "0.75rem" },
                                    px: { xs: 1, sm: 2 },
                                    minWidth: { xs: 32, sm: "auto" },
                                    "& .MuiButton-startIcon": {
                                        marginLeft: dir === "rtl" ? "0.3rem" : "-4px",
                                        marginRight: dir === "rtl" ? "0px" : "8px"
                                    },
                                }}
                            >
                                {isMobile ? "T" : t("translated_text")}
                            </Button>
                        )}
                    </ButtonGroup>
                </Box>

                <React.Fragment>
                    <Box sx={{display: "flex", alignItems: "center", gap: 1 }}>

                        <RegenerateServicesPopup digitizationId={digitizationId} currentPageNum={pageNumber}
                                                 numPages={numPages} fetchData={fetchData}/>

                        {/*<SaveButton updatedPages={updatedPages} handleSave={handleSaveWithLoading} disabled={loading}
                                    t={t} dir={dir}/>*/}


                        {isEditing ? (
                            <Tooltip title={t('save_changes')}>
                                <span>
                                    <Button
                                        onClick={handleSaveWithLoading}
                                        startIcon={<FaSave size={isMobile ? 10 : 12}/>}
                                        sx={{
                                            height: { xs: 28, sm: 32 },
                                            width: { xs: 28, sm: 32 },
                                            bgcolor: "transparent",
                                            color: "#616161",
                                            border: "1px solid #BDBDBD",
                                            "&:hover": {
                                                borderColor: "#757575",
                                                bgcolor: "#F5F5F5",
                                            },
                                            "& .MuiButton-startIcon": {
                                                marginLeft: dir === "rtl" ? "0.3rem" : "-4px",
                                                marginRight: dir === "rtl" ? "0px" : "8px"
                                            },
                                        }}
                                    >
                                    {t('save')}
                                    </Button>
                                </span>
                            </Tooltip> ) : (
                            <Tooltip title={t('edit')}>
                                <span>
                                    <Button
                                        onClick={handleToggleEdit}
                                        startIcon={<FaEdit size={isMobile ? 10 : 12}/>}
                                        sx={{
                                            height: { xs: 28, sm: 32 },
                                            width: { xs: 28, sm: 32 },
                                            bgcolor: "transparent",
                                            color: "#616161",
                                            border: "1px solid #BDBDBD",
                                            "&:hover": {
                                                borderColor: "#757575",
                                                bgcolor: "#F5F5F5",
                                            },
                                            "& .MuiButton-startIcon": {
                                                marginLeft: dir === "rtl" ? "0.3rem" : "-4px",
                                                marginRight: dir === "rtl" ? "0px" : "8px"
                                            },
                                        }}
                                    >
                                    {t('edit')}
                                    </Button>
                                </span>
                            </Tooltip>
                        )}

                    </Box>
                </React.Fragment>

            </Box>

            <div style={{ padding: "0px 0px 0px 15px", flexGrow: 1}}>
                {loading && (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(255,255,255,0.7)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10
                    }}>
                        <CircularProgress/>
                    </div>
                )}

                <SoftBox style={{
                    direction: displayExtractedText ? getScriptDirectionJoditEditor(sourceLanguage) : getScriptDirectionJoditEditor(targetLanguage),
                    height: '100%'
                }}>

                    {displayExtractedText ?

                        <React.Fragment>
                            {isEditing? (
                                <JoditEditor
                                    ref={editor}
                                    value={htmlContent}
                                    config={config}
                                    onReady={(editorInstance) => {
                                        editor.current = editorInstance;
                                    }}
                                    onChange={(newContent) => {
                                        pushToHistory(pageNumber, newContent, "html");
                                    }}
                                    onBlur={(newContent) => updateHtml(newContent)}
                                />
                            ) : (
                                <iframe
                                    style={{
                                        border: '1px solid #eee',
                                        padding: '15px',
                                        marginTop: '10px',
                                        backgroundColor: '#fafafa',
                                        borderRadius: '5px',
                                        overflowX: 'auto',
                                        height: "100%",
                                        width: "100%"
                                    }}
                                    srcDoc={`
                                        <!DOCTYPE html>
                                        <html lang="ar" dir="rtl">
                                        <head>
                                        <meta charset="utf-8">
                                        <script type="text/x-mathjax-config">
                                        MathJax.Hub.Config({
                                            tex2jax: {
                                            inlineMath: [['$', '$'], ['\\\\(', '\\\\)']],
                                            displayMath: [['$$', '$$'], ['\\\\[', '\\\\]']],
                                            processEscapes: true
                                            },
                                            TeX: {
                                            extensions: ["AMSmath.js", "AMSsymbols.js"]
                                            },
                                            "HTML-CSS": {
                                            linebreaks: { automatic: true }
                                            }
                                        });
                                        </script>
                                        <script src="https://cdn.jsdelivr.net/npm/mathjax@2/MathJax.js?config=TeX-AMS_HTML"></script>
                                        <script src="https://cdn.jsdelivr.net/gh/Edraak/arabic-mathjax@master/dist/arabic.js"></script>
                                        </head>
                                        <body>
                                        ${injectArticleInner(fullHtmlTemplateRef.current, htmlContent)}
                                        </body>
                                        </html>
                                    `}
                                />
                            )}
                        </React.Fragment> :
                        <React.Fragment>
                            {isEditing? (
                                <JoditEditor
                                    ref={editor}
                                    value={translatedContent}
                                    config={config}
                                    onReady={(editorInstance) => {
                                        editor.current = editorInstance;
                                    }}
                                    onChange={(newContent) => {
                                        pushToHistory(pageNumber, newContent, "translated_text");
                                    }}
                                    onBlur={(newContent) => updateTranslatedHtml(newContent)}
                                />
                            ) : (
                                <iframe
                                    style={{
                                        border: '1px solid #eee',
                                        padding: '15px',
                                        marginTop: '10px',
                                        backgroundColor: '#fafafa',
                                        borderRadius: '5px',
                                        overflowX: 'auto',
                                        height: "100%",
                                        width: "100%"
                                    }}
                                    srcDoc={`
                                        <!DOCTYPE html>
                                        <html lang="ar" dir="rtl">
                                        <head>
                                        <meta charset="utf-8">
                                        <script type="text/x-mathjax-config">
                                        MathJax.Hub.Config({
                                            tex2jax: {
                                            inlineMath: [['$', '$'], ['\\\\(', '\\\\)']],
                                            displayMath: [['$$', '$$'], ['\\\\[', '\\\\]']],
                                            processEscapes: true
                                            },
                                            TeX: {
                                            extensions: ["AMSmath.js", "AMSsymbols.js"]
                                            },
                                            "HTML-CSS": {
                                            linebreaks: { automatic: true }
                                            }
                                        });
                                        </script>
                                        <script src="https://cdn.jsdelivr.net/npm/mathjax@2/MathJax.js?config=TeX-AMS_HTML"></script>
                                        <script src="https://cdn.jsdelivr.net/gh/Edraak/arabic-mathjax@master/dist/arabic.js"></script>
                                        </head>
                                        <body>
                                        ${injectArticleInner(fullTranslatedTextTemplateRef.current, translatedContent)}
                                        </body>
                                        </html>
                                    `}
                                />
                            )}
                        </React.Fragment>}

                </SoftBox>


            </div>
            {displayExtractedText && audioUrl && !loading && (
                <AudioPlayer audioUrl={audioUrl}/>
            )}
            <ChatBot digitizationId={digitizationId} />
        </div>
    );
};

DigitizationHtmlComponent.propTypes = {
    containerWidth: PropTypes.string,
    pageNumber: PropTypes.number,
    digitizationId: PropTypes.number,
    configWidth: PropTypes.string,
    updateConfigWidth: PropTypes.func,
    page: PropTypes.object,
    sourceLanguage: PropTypes.string,
    targetLanguage: PropTypes.string,
    addOrUpdatePages: PropTypes.func,
    updatedPages: PropTypes.array,
    handleSave: PropTypes.func,
    bookmarkingSelected: PropTypes.bool,
    translationSelected: PropTypes.bool,
    diacriticsSelected: PropTypes.bool,
    diacritizeWords: PropTypes.func,
    regenerateBookmarking: PropTypes.func,
    numPages: PropTypes.number,
    fileName: PropTypes.string,
    fetchData: PropTypes.func,
    setPage: PropTypes.func,
    setNotification: PropTypes.func
};

export default DigitizationHtmlComponent;